import { useState } from "react";
import axios from "axios";
import {
  Paper, List, ListItemButton, ListItemText, Typography, CircularProgress,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { API_BASE_URL } from "../config";

export const LINKS_API = `${API_BASE_URL}/links`;

// What a link points at, for labelling: an uploaded file or an external address.
export const linkTarget = (link) => link.url || link.document_filename || "—";

/**
 * Open a link in a new tab.
 *
 * External URLs open directly. Uploaded documents need the bearer token, so a
 * plain href would 401 — fetch the file via axios (token attached by the request
 * interceptor) and point the tab at a blob URL instead. Either way the tab is
 * opened synchronously on click so the browser doesn't block it as a popup.
 */
export async function openLink(link) {
  if (link.url) {
    window.open(link.url, "_blank", "noopener,noreferrer");
    return;
  }
  const tab = window.open("", "_blank");
  try {
    const res = await axios.get(`${LINKS_API}/${link.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    if (tab) tab.location = url; else window.open(url, "_blank");
    // Give the tab time to load before releasing the object URL.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    if (tab) tab.close();  // the axios interceptor has already surfaced a toast
  }
}

/**
 * Read-only list of links: titles that open in a new tab. Used by the
 * user-facing Guidelines page and the My events detail view.
 */
export default function LinkList({ links, empty }) {
  const [busyId, setBusyId] = useState(null);

  if (!links.length) {
    return <Typography color="text.secondary">{empty}</Typography>;
  }

  const open = (link) => {
    setBusyId(link.id);
    Promise.resolve(openLink(link)).finally(() => setBusyId(null));
  };

  return (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
      <List disablePadding>
        {links.map(link => (
          <ListItemButton key={link.id} onClick={() => open(link)}>
            <DescriptionIcon fontSize="small" sx={{ mr: 1.5, color: "text.secondary" }} />
            <ListItemText primary={link.title} />
            {busyId === link.id
              ? <CircularProgress size={14} />
              : <OpenInNewIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );
}
